import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { EgLauncher, EgTopbar, EgUserMenu, EgUserMenuItem, EgUserMenuLabels } from '@expertgroup/shell';
import { AppBreadcrumb } from './app.breadcrumb';
import { GlobalSearchComponent } from '@/app/layout/global-search/global-search.component';
import { GlobalSearchService } from '@/app/layout/global-search/global-search.service';
import { AuthService } from '@/app/core/auth/auth.service';
import { CurrentUserService } from '@/app/core/current-user/current-user.service';
import { PERSONA_PATHS, PersonaService } from '@/app/core/current-user/persona.service';
import { I18n } from '@/app/core/i18n/i18n';
import { MyApplication, MyApplicationsService } from '@/app/core/my-applications/my-applications.service';

/**
 * Topbar. Visina, razmaci i primitivi dolaze iz ljuske; sadrzaj ulazi kroz
 * slotove `start` / `center` / `end`.
 *
 *   KORISNICKI MENI (shell 0.13). Jedan `<eg-user-menu>` zamenjuje rucni avatar
 *   blok, zaseban prekidac jezika i `<eg-theme-toggle>`: profil („Moj nalog"),
 *   jezik (SR/EN preko `I18n`), tamna tema (ljuskin `EgThemeService`, aplikacija
 *   ne vezuje nista) i odjava. Tekstovi menija idu kroz prevode aplikacije, u oba
 *   jezika. Stari okidac je bio `<a>` bez href-a — tastaturom nedostizan.
 *
 *   LAUNCHER. Meni aplikacija ekosistema, isti u svih pet aplikacija (shell 0.12+):
 *   samo UI aplikacije kojima trenutni korisnik ima pristup u Identity-ju i koje
 *   imaju registrovan UI klijent. Pristup i adrese daje `launcherProviders`
 *   (core/launcher); bez njega launcher nema nijedan link.
 *
 * ⚠️ Launcher i „Moje aplikacije" NISU ista stvar i namerno stoje odvojeno.
 * Launcher je zajednicki katalog ljuske — isti redosled i iste ikone svuda, jedan
 * link po aplikaciji. „Moje aplikacije" je lista zakupa ove aplikacije, sa admin
 * oznakama i svim klijentima po aplikaciji; nijedna druga aplikacija je nema. Ona
 * zivi u korisnickom meniju (odluka vlasnika proizvoda, 0.13): grupa „Moje
 * aplikacije", u njoj po jedna imenovana grupa po aplikaciji sa linkovima klijenata
 * kao `egUserMenuItem`. Unutar `role="menu"` stoje samo stavke i imenovane grupe;
 * admin oznaka je deo imena grupe, ne samo bedz. Lista ima sopstveni skrol.
 *
 * GLOBALNA PRETRAGA (UX pregled 1, D15). U sredini topbara, izmedju ljuskinog
 * razmaka i drugog razmaka posle nje (EgTopbar ima samo jedan, pred `center`),
 * pa stoji na sredini izmedju mrvica i korisnickog menija. Okidac i Ctrl/Cmd+K
 * postoje samo kad paleta za ovog aktera ima bar jednu grupu
 * (`GlobalSearchService.available`): operater, i administrator dva tenanta ili
 * vise (samo tenanti, preko svog portfolija). Ona je zamenila i skok na tenant i
 * mrtvo polje pretrage; zvonce bez funkcije i separator su otisli sa njima.
 *
 * Hamburger je otisao sa menu modovima: sina je jedna i uvek vidljiva.
 */
@Component({
    selector: '[app-header]',
    standalone: true,
    imports: [EgTopbar, EgLauncher, EgUserMenu, EgUserMenuItem, AppBreadcrumb, GlobalSearchComponent],
    template: `
        <eg-topbar>
            <eg-launcher start />
            <div start app-breadcrumb></div>

            <!-- The global search, for the personas with at least one group (D15). The second spacer centres it between the
                 breadcrumb and the user menu; without the search the two spacers simply share the gap. -->
            @if (globalSearch.available()) {
                <eg-global-search center />
            }
            <span center class="eg-topbar__spacer"></span>

            <eg-user-menu
                end
                [name]="userName()"
                [role]="roleLabel()"
                [initials]="currentUser.displayName() ? '' : 'EG'"
                [language]="languageCode()"
                (languageChange)="setLanguage($event)"
                [labels]="menuLabels()"
                (profile)="openProfile()"
                (signOut)="logout()"
            >
                @if (menuApplications().length > 0) {
                    <div class="eg-user-menu__sep" role="separator"></div>
                    <div role="group" class="id-user-menu__apps" [attr.aria-labelledby]="applicationsLabelId">
                        <div class="eg-eyebrow id-user-menu__label" [id]="applicationsLabelId">{{ i18n.t('navigation.myApplications') }}</div>
                        <div class="id-user-menu__scroll">
                            @for (app of menuApplications(); track app.applicationId) {
                                <div role="group" class="id-user-menu__app" [attr.aria-label]="applicationGroupName(app)">
                                    <div class="id-user-menu__app-name">
                                        <span class="id-user-menu__app-title">{{ app.application.name }}</span>
                                        @if (app.isApplicationSuperAdmin) {
                                            <span class="eg-badge eg-badge--neutral">{{ i18n.t('layout.applicationAdminTag') }}</span>
                                        }
                                    </div>
                                    @for (client of app.clients; track client.address) {
                                        <a egUserMenuItem [href]="client.address" target="_blank" rel="noopener">
                                            <i class="ph-fill ph-arrow-square-out eg-user-menu__icon" aria-hidden="true"></i>
                                            <span class="eg-user-menu__text">{{ client.name }}</span>
                                        </a>
                                    }
                                </div>
                            }
                        </div>
                    </div>
                }
            </eg-user-menu>
        </eg-topbar>
    `
})
export class AppHeader implements OnInit {
    readonly currentUser = inject(CurrentUserService);
    readonly persona = inject(PersonaService);
    readonly globalSearch = inject(GlobalSearchService);
    private readonly auth = inject(AuthService);
    private readonly router = inject(Router);
    private readonly myApplicationsService = inject(MyApplicationsService);
    readonly i18n = inject(I18n);

    /** The signed-in user's applications; only those with a client to open are offered in the user menu. */
    readonly myApplications = signal<MyApplication[]>([]);

    /** An application without a client has nothing to open, and an empty group inside a menu would announce nothing. */
    readonly menuApplications = computed(() => this.myApplications().filter((app) => app.clients.length > 0));

    readonly applicationsLabelId = 'id-user-menu-applications';

    /**
     * `name` is required by the menu. The shell renders once `currentUserGuard` has run, but a failed `Me` leaves the actor
     * null (the `unresolved` persona) — the menu still renders, with a generic name, so sign-out stays reachable.
     */
    readonly userName = computed(() => this.currentUser.displayName() || this.i18n.t('common.labels.user'));

    /** The line under the name: who the actor is to this UI (the persona), never guessed for an `unresolved` one. */
    readonly roleLabel = computed(() => {
        switch (this.persona.kind()) {
            case 'operator':
                return this.i18n.t(this.currentUser.isSuperAdmin() ? 'layout.role.superAdmin' : 'layout.role.operator');
            case 'tenantAdmin':
                return this.i18n.t('layout.role.tenantAdmin');
            case 'user':
                return this.i18n.t('layout.role.user');
            case 'unresolved':
                return '';
        }
    });

    /** The menu speaks the app's language: its four texts come from the app's own copy, in both locales. */
    readonly menuLabels = computed<EgUserMenuLabels>(() => ({
        profile: this.i18n.t('navigation.myAccount'),
        language: this.i18n.t('language.current'),
        darkTheme: this.i18n.t('layout.darkTheme'),
        signOut: this.i18n.t('common.actions.signOut')
    }));

    /** The menu's codes are `sr` / `en`; the app's locales are `sr-Latn` / `en`. */
    readonly languageCode = computed(() => (this.i18n.locale() === 'en' ? 'en' : 'sr'));

    async ngOnInit(): Promise<void> {
        // Best-effort: the user menu must never block the shell — on failure the section simply stays hidden
        // (the global Apollo errorLink already surfaces the error toast).
        try {
            this.myApplications.set(await this.myApplicationsService.list());
        } catch {
            this.myApplications.set([]);
        }
    }

    /** The application's group name: the admin marker is part of it, not only a badge. */
    applicationGroupName(app: MyApplication): string {
        return app.isApplicationSuperAdmin ? this.i18n.t('layout.applicationAdmin', { name: app.application.name }) : app.application.name;
    }

    setLanguage(code: string): void {
        this.i18n.setLocale(code === 'en' ? 'en' : 'sr-Latn');
    }

    openProfile(): void {
        void this.router.navigateByUrl(PERSONA_PATHS.myAccount);
    }

    logout(): void {
        // Drop the actor and every cached tenant capability before the end-session redirect.
        this.currentUser.reset();
        this.auth.logout();
    }
}
